//#region types
import { FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { GenericFormSection } from "models/propTypes/genericFormProps";
import { DocumentDraftFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { SelectionItem } from "models/utilTypes/selectionTypes";
//#endregion
//#region components
import { useDocumentDraftService } from "serviceHooks";
//#endregion
import { FilterHelper, QueryStateHelper } from "utils/helpers";

function useDocumentDraftFilterDefinition() {
  const documentDraftService = useDocumentDraftService();
  const apiToReportFilterFormatter = async (filter: WintSuperkollAPIModel.DocumentDraftFilter) => {
    const reportFilter: DocumentDraftFormTypes.Filter = {};

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.DocumentDraft.Filter.Date,
      filter.sentDateFrom,
      filter.sentDateTo
    );

    if (filter.states !== undefined) {
      reportFilter.state = documentDraftService
        .getDocumentDraftStatuses()
        .filter((s) => filter.states?.includes(s.id as number)) as SelectionItem[];
    }

    if (filter.types !== undefined) {
      reportFilter.type = documentDraftService
        .getDocumentDraftTypes()
        .filter((s) => filter.types?.includes(s.id as number)) as SelectionItem[];
    }

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.DocumentDraft.Filter.Amount,
      filter.minAmount,
      filter.maxAmount
    );

    if (filter.from !== undefined) {
      reportFilter.from = filter.from;
    }

    if (filter.subject !== undefined) {
      reportFilter.subject = filter.subject;
    }

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<DocumentDraftFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,

      innerFields: {
        date: {
          label: TranslationKeys.Common.Date,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        state: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: documentDraftService.getDocumentDraftStatuses(),
          },
        },
        type: {
          label: TranslationKeys.Common.Type,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: documentDraftService.getDocumentDraftTypes(),
          },
        },
        source: {
          label: TranslationKeys.Common.Source,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: documentDraftService.getDocumentDraftSources(),
          },
        },
        amount: {
          label: TranslationKeys.Common.Amount,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
        },
        from: {
          label: TranslationKeys.Common.From,
          type: FormEnums.FormFieldType.TEXT,
        },
        subject: {
          label: TranslationKeys.Common.Subject,
          type: FormEnums.FormFieldType.TEXT,
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<DocumentDraftFormTypes.Filter> = {
    date: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.DocumentDraft.Filter.Date),
    state: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.DocumentDraft.Filter.State,
      documentDraftService.getDocumentDraftStatuses().map((i) => ({ id: i.id ?? 0, value: i.value }))
    ),
    type: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.DocumentDraft.Filter.Type,
      documentDraftService.getDocumentDraftTypes().map((i) => ({ id: i.id ?? 0, value: i.value }))
    ),
    amount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.DocumentDraft.Filter.Amount),
    from: QueryStateHelper.stringQueryEncoder(FormFieldConstants.DocumentDraft.Filter.From),
    subject: QueryStateHelper.stringQueryEncoder(FormFieldConstants.DocumentDraft.Filter.Subject),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useDocumentDraftFilterDefinition;
